import React, { useEffect, useState } from 'react';
import { Package, Warehouse, AlertTriangle, Activity, Plus, ArrowRight, Users, Building2, Truck, CheckCircle, AlertCircle } from 'lucide-react';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const InventoryDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, token } = useAuth();

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [stats, setStats] = useState({
    totalProducts: 0,
    warehouses: 0,
    lowStockItems: 0,
    activeAlerts: 0,
  });

  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

   

  useEffect(() => {
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const fetchWithRetry = async (url, headers, retries = 4, baseDelay = 7000) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          await delay(7000); // wait 7 seconds before each call
          const res = await fetch(url, { headers });

          if (res.status === 429) {
            const jitter = Math.floor(Math.random() * 500);
            const wait = baseDelay * Math.pow(2, attempt - 1) + jitter;
            console.warn(`⚠️ Rate limited: ${url}, retrying in ${wait}ms (attempt ${attempt})`);
            await delay(wait);
            continue;
          }

          if (!res.ok) throw new Error(`HTTP ${res.status} on ${url}`);
          return await res.json();
        } catch (err) {
          if (attempt === retries) throw err;
          const wait = baseDelay * Math.pow(2, attempt - 1);
          console.warn(`Retrying ${url} (${attempt}/${retries}) after ${wait}ms`);
          await delay(wait);
        }
      }
    };

    const fetchDashboardStats = async () => {
      setLoading(true);
      setError("");

      try {
        const cachedData = sessionStorage.getItem("dashboardStats");

        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          const age = Date.now() - timestamp;

          if (age < 10 * 60 * 1000) { // 10 minutes freshness
             
            setStats(data.stats);
            setLowStockAlerts(data.lowStockAlerts);
            setLoading(false);
            return;
          }
        }

        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        // Sequential fetches with 7s delay
        const productsData = await fetchWithRetry(`${API_BASE}/Products`, headers);
        const warehousesData = await fetchWithRetry(`${API_BASE}/Warehouse`, headers);
        const inventoryData = await fetchWithRetry(`${API_BASE}/Inventory`, headers);
        const lowStockData = await fetchWithRetry(`${API_BASE}/Inventory/low-stock`, headers);

        // Normalize responses
        const products = Array.isArray(productsData) ? productsData : [];
        const warehouses = Array.isArray(warehousesData) ? warehousesData : [];
        const inventory = Array.isArray(inventoryData) ? inventoryData : [];
        const lowStockItems = Array.isArray(lowStockData) ? lowStockData : [];

        const lowStockCount =
          typeof lowStockData === "object"
            ? lowStockData.count || lowStockData.total || lowStockData.value || lowStockItems.length
            : 0;

        const newStats = {
          totalProducts: products.length,
          warehouses: warehouses.length,
          lowStockItems: lowStockCount,
          activeAlerts: lowStockCount,
        };

        setStats(newStats);
        setLowStockAlerts(lowStockItems);

         sessionStorage.setItem(
            "dashboardStats",
            JSON.stringify({
              data: { stats: newStats, lowStockAlerts: lowStockItems },
              timestamp: Date.now(),
            })
          );


         
         
         
         
      } catch (err) {
        console.error("❌ Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please wait and try again.");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchDashboardStats();
  }, [token]);



  const handleViewAll = () => {
    navigate('/inventory');
  };

  const StatCard = ({ title, value, icon: Icon, colorClass, href }) => (
    <a href={href} className={`stat-card ${colorClass}`}>
      <div className="stat-card-content">
        <div className="stat-info">
          <p className="stat-title">{title}</p>
          <p className="stat-value">{loading ? '...' : value}</p>
        </div>
        <Icon className="stat-icon" strokeWidth={1.5} />
      </div>
    </a>
  );

  const ActionCard = ({ title, description, icon: Icon, href }) => (
    <a href={href} className="action-card">
      <div className="action-card-content">
        <div className="action-card-left">
          <div className="action-icon-wrapper">
            <Icon className="action-icon" />
          </div>
          <div className="action-text">
            <h3 className="action-title">{title}</h3>
            <p className="action-description">{description}</p>
          </div>
        </div>
        <ArrowRight className="action-arrow" />
      </div>
    </a>
  );

  return (
    <div className="dashboard-container">
      {/* Header with user info and logout */}
      <div className='info-header'>
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} • {user?.role}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Test Roles Button 
          <button 
            onClick={() => navigate('/test-roles')}
            style={{
              background: '#2d5016',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            🧪 Test Roles
          </button>*/}
          
          {/* Logout Button */}
          <button 
            onClick={logout}
            style={{
              background: '#ff8c00',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="dashboard-title">Inventory Management</h1>
            <p className="dashboard-subtitle">Smart prompt-based inventory control system</p>
          </div>
          <button className="view-all-btn" onClick={handleViewAll}>
            ☰ View All
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-content">
          {error && (
            <div className="error-banner">
              <p>{error}</p>
            </div>
          )}

          {/* Stats Grid */}
          <div className="stats-grid">
            <StatCard
              title="Total Products"
              value={stats.totalProducts}
              icon={Package}
              colorClass="stat-orange"
              href="/products"
            />
            <StatCard
              title="Warehouses"
              value={stats.warehouses}
              icon={Warehouse}
              colorClass="stat-green"
              href="/warehouse"
            />
            <StatCard
              title="Low Stock Items"
              value={stats.lowStockItems}
              icon={AlertTriangle}
              colorClass="stat-yellow"
              href="/inventory"
            />
            <StatCard
              title="Active Alerts"
              value={stats.activeAlerts}
              icon={Activity}
              colorClass="stat-red"
              href="/alerts"
            />
          </div>

          {/* Attention Alert */}
          <div className="attention-alert">
            <div className="alert-content">
              <div className="alert-icon-circle">
                <Activity className="alert-icon" />
              </div>
              <h2 className="alert-heading">Attention Needed</h2>
              <p className="alert-text">
                {stats.lowStockItems} item(s) are running low or out of stock
              </p>
              
              {/* Show low stock items if available */}
              {lowStockAlerts.length > 0 && (
                <div className="low-stock-list">
                  <h4 className="low-stock-title">Low Stock Items:</h4>
                  <div className="low-stock-items">
                    {lowStockAlerts.slice(0, 5).map((item, index) => (
                      <div key={item.id || index} className="low-stock-item">
                        <span className="product-name">{item.productName || `Product ${item.productId}`}</span>
                        <span className="warehouse-name">{item.warehouseName || `Warehouse ${item.warehouseId}`}</span>
                        <span className="stock-level">Stock: {item.units || 0}</span>
                      </div>
                    ))}
                    {lowStockAlerts.length > 5 && (
                      <div className="more-items">
                        +{lowStockAlerts.length - 5} more items...
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <a href="/inventory" className="alert-btn">
                View Inventory
              </a>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions-section">
            <h2 className="section-title">Quick Actions</h2>
            <div className="actions-grid">
              <ActionCard
                title="Products"
                description="Manage products in your catalog"
                icon={Plus}
                href="/products"
              />
              <ActionCard
                title="Warehouses"
                description="Manage your storage locations"
                icon={Warehouse}
                href="/warehouse"
              />
              <ActionCard
                title="Suppliers"
                description="Manage you suppliers"
                icon={Truck}
                href="/suppliers"
              />
              <ActionCard
                title="View Inventory"
                description="See all products and stock levels"
                icon={Package}
                href="/inventory"
              />
              <ActionCard
                title="Stock Movement"
                description="Track all stock transactions"
                icon={Activity}
                href="/stock"
              />
              <ActionCard
                title="Quality Assurance"
                description="Monitor and manage all the stock movements"
                icon={CheckCircle}
                href="/quality-assurance"
              />
              
              {/* Admin Only Actions */}
              {isAdmin && (
                <>
                  <ActionCard
                    title="Team Management"
                    description="Manage team members and permissions"
                    icon={Users}
                    href="/team"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InventoryDashboard;